import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { WorkOrderDetailsDto, StatusCodeDto } from '@app/generated/model';
import { WorkLineModel, WorkOrderObjectModel } from './work-order-object.model';
import { UntypedFormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  getCurrentWorkOrder,
  getHierarchy,
  getSelectedObjects,
  getSelectedWorkLines,
} from '@app/core/store/selectors/work-order.selector';
import {
  DeselectAllObjects,
  DeSelectObject,
  DeselectWorkLine,
  LoadWorkOrder,
  LoadWorkOrderObjects,
  SelectAllObjects,
  SelectObject,
  SelectWorkLine,
} from '@dwo/actions';
import { ActivatedRoute, Router } from '@angular/router';
import {
  WorkLineFilters,
  collectProgressTypes,
  collectWorkTypes,
  createEmptyWorkLineFilters,
  hasActiveWorkLineFilters,
  objectHasMatch,
} from './work-line-filter';

@Component({
  selector: 'app-object-hierarchy',
  templateUrl: './object-hierarchy.component.html',
  styleUrls: ['./object-hierarchy.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ObjectHierarchyComponent implements OnInit, OnDestroy {
  @Output() objectSelectionChanged = new EventEmitter<any>();
  public workOrder: WorkOrderDetailsDto;

  public selectAllObjects = new UntypedFormControl(false);

  public hasSelectedItems: boolean;
  public objects: WorkOrderObjectModel[];
  public statusCodes: StatusCodeDto[];

  public filters: WorkLineFilters = createEmptyWorkLineFilters();
  public visibleObjects: WorkOrderObjectModel[] = [];
  public workTypeOptions: string[] = [];
  public progressTypeOptions: string[] = [];

  private selectedWorkLines = new Set<WorkLineModel>();
  private selectedObjects = new Set<WorkOrderObjectModel>();
  private workOrderId: string = null;

  private onDestroy = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
  ) {}

  ngOnInit() {
    this.route.parent?.parent?.params.pipe(takeUntil(this.onDestroy)).subscribe((params) => {
      const workOrderId = params['id'];
      if (workOrderId) {
        this.workOrderId = workOrderId;
      }
    });

    this.store
      .select(getCurrentWorkOrder)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((workOrder) => {
        if (workOrder) {
          this.workOrder = workOrder;
          this.store.dispatch(new LoadWorkOrderObjects(this.workOrder.id));
        } else {
          if (this.workOrderId) {
            this.store.dispatch(new LoadWorkOrder(this.workOrderId));
          }
        }
      });

    this.store
      .select(getHierarchy)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((hierarchy) => {
        if (hierarchy && hierarchy.length > 0) {
          const objects = hierarchy.map((dto) => WorkOrderObjectModel.fromDto(dto));
          this.objects = objects;
          this.workTypeOptions = collectWorkTypes(objects);
          this.progressTypeOptions = collectProgressTypes(objects);
          this.applyFilters();
        }
      });

    this.store
      .select(getSelectedObjects)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((selectedObjects) => {
        if (selectedObjects.length > 0) {
          this.hasSelectedItems = true;
          this.selectedObjects = new Set(selectedObjects);
        } else {
          this.hasSelectedItems = false;
          this.selectedObjects = new Set();
        }
      });

    this.store
      .select(getSelectedWorkLines)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((selectedWorkLines) => {
        if (selectedWorkLines.length > 0) {
          this.hasSelectedItems = true;
          this.selectedWorkLines = new Set(selectedWorkLines);
        } else {
          this.hasSelectedItems = false;
          this.selectedWorkLines = new Set();
        }
      });

    //   this.statusCodes$ = this.statusCodesService.getStatusCodes();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
    this.store.dispatch(new DeselectAllObjects());
  }

  applyFilters(): void {
    this.visibleObjects = (this.objects ?? []).filter((o) => objectHasMatch(o, this.filters));
  }

  hasActiveFilters(): boolean {
    return hasActiveWorkLineFilters(this.filters);
  }

  clearFilters(): void {
    this.filters = createEmptyWorkLineFilters();
    this.applyFilters();
  }

  clearSearch(): void {
    this.filters.search = '';
    this.applyFilters();
  }

  clearSelection() {
    this.selectAllObjects.setValue(false);
    this.store.dispatch(new DeselectAllObjects());
  }

  toggleAllObjects(): void {
    if (this.selectAllObjects.value) {
      this.selectAll();
    } else {
      this.clearSelection();
    }
  }

  selectAll() {
    this.store.dispatch(new SelectAllObjects(this.objects));
  }

  addSelectedObject(object: WorkOrderObjectModel) {
    const hasWeeklyProgress = this.workOrder.showWeeklyProgress;

    object.isSelected = true;
    this.store.dispatch(new SelectObject(object));

    object.workLines
      .filter((c) => (hasWeeklyProgress ? c.lockedProgress !== 100 : true))
      .forEach((wl) => this.addSelectedWorkLine(wl));
    object.children
      .filter((c) => (hasWeeklyProgress ? c.lockedProgress !== 100 : true))
      .forEach((c) => this.addSelectedObject(c));
  }

  removeSelectedObject(object: WorkOrderObjectModel) {
    object.isSelected = false;
    this.store.dispatch(new DeSelectObject(object));
    object.workLines.forEach((wl) => this.removeSelectedWorkLine(wl));
    object.children.forEach((c) => this.removeSelectedObject(c));
    if (this.selectedObjects.size === 0) {
      this.hasSelectedItems = false;
    }
  }

  addSelectedWorkLine(workLine: WorkLineModel) {
    workLine.isSelected = true;
    this.store.dispatch(new SelectWorkLine(workLine));
  }

  removeSelectedWorkLine(workLine: WorkLineModel) {
    workLine.isSelected = false;
    this.store.dispatch(new DeselectWorkLine(workLine));
  }
}
